import { useMemo, useRef } from 'react';

import { isQuoteForCurrentSelection } from '@/components/TransactionModule/MarketMode/utils';
import {
  calculateGrossBuyAmount,
  calculatePriceImpact,
} from '@/components/TransactionModule/utils';

import { getQuoteRefreshInterval } from '@/constants/chain';

import { useIntents } from '@/hooks/useIntents';
import { useSwap, type SwapArgs } from '@/hooks/useSwap';

import type { Price, Quote } from '@/utils/0x.types';
import type { TokenResult } from '@/utils/0x/token-registry.types';

import { emptyQuoteFields, type MarketTradeQuote, type TokenUsdPrices } from './tradeQuote';

export type MarketTradeQuoteParams = {
  /** The market lane that is authoritative while the engine's source is market. */
  lane: 'swap' | 'intents';
  /** False while another source (cross-chain/limit) owns the trade; no market fetch fires. */
  enabled: boolean;
  /** Whether a pair and a positive sell amount are selected. */
  hasTradeInputs: boolean;
  /** The immediate selection; payloads are only served while they match it. */
  queryArgs: SwapArgs | null;
  /** The debounced selection the aggregators fetch against. */
  debouncedQueryArgs: SwapArgs | null;
  pairKey: string;
  chainId: number;
  isGasless: boolean;
  buyAmountDecimal: string;
  buyAmountUsd: number;
  pausePolling: boolean;
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
  tokenUsdPrices: TokenUsdPrices;
};

/**
 * Market lane provider: runs the swap and intents aggregators and normalizes
 * the authoritative lane's payload into a `MarketTradeQuote`. A payload is
 * accepted only when it matches the immediate selection and is served only
 * while that identity (lane + pair + sell amount) is still current, so amount
 * edits, pair changes, and lane switches never expose a previous payload.
 */
export function useMarketTradeQuote({
  lane,
  enabled,
  hasTradeInputs,
  queryArgs,
  debouncedQueryArgs,
  pairKey,
  chainId,
  isGasless,
  buyAmountDecimal,
  buyAmountUsd,
  pausePolling,
  sellToken,
  buyToken,
  tokenUsdPrices,
}: MarketTradeQuoteParams): MarketTradeQuote {
  const query = enabled ? debouncedQueryArgs : null;
  const telemetry = useMemo(
    () => ({ buyAmountDecimal, buyAmountUsd }),
    [buyAmountDecimal, buyAmountUsd],
  );
  const options = useMemo(
    () => ({ refetchInterval: pausePolling ? (false as const) : getQuoteRefreshInterval(chainId) }),
    [chainId, pausePolling],
  );
  const swapResult = useSwap({ query, isGasless, telemetry, options });
  const intentsResult = useIntents({ query, isGasless, telemetry, options });
  const result = lane === 'intents' ? intentsResult : swapResult;

  const identityKey = `${lane}|${pairKey}|${queryArgs?.sellAmount ?? ''}`;
  const matchesCurrentSelection = Boolean(
    result.data &&
      queryArgs &&
      isQuoteForCurrentSelection(result.data, {
        sellAmount: queryArgs.sellAmount,
        sellTokenAddress: queryArgs.sellTokenAddress,
        buyTokenAddress: queryArgs.buyTokenAddress,
        chainId: queryArgs.sellTokenChainId,
      }),
  );
  const acceptedRef = useRef<{ key: string; payload: Price | Quote } | undefined>(undefined);
  if (result.data && matchesCurrentSelection) {
    acceptedRef.current = { key: identityKey, payload: result.data };
  }
  const accepted =
    acceptedRef.current?.key === identityKey ? acceptedRef.current.payload : undefined;

  return useMemo<MarketTradeQuote>(() => {
    const idle = (error?: Error): MarketTradeQuote => ({
      ...emptyQuoteFields(error),
      source: lane,
      status: error ? 'error' : 'idle',
    });

    if (!hasTradeInputs || !sellToken || !buyToken) return idle();
    const error = result.error ?? undefined;
    if (error) return idle(error);
    const sellAmount = queryArgs?.sellAmount;
    if (!accepted || !sellAmount) {
      return {
        ...idle(),
        status: 'loading',
        isStale: Boolean(result.data && !matchesCurrentSelection),
      };
    }
    const buyAmount = calculateGrossBuyAmount({
      priceOrQuote: accepted,
      sellTokenDecimals: sellToken.decimals,
      buyTokenDecimals: buyToken.decimals,
    });
    return {
      source: lane,
      status: 'fetched',
      buyAmount,
      priceImpact: calculatePriceImpact({
        buyToken,
        sellToken,
        buyAmount,
        sellAmount,
        tokenUsdPrices,
        buyTaxBps: accepted.tokenMetadata.buyToken.buyTaxBps,
        sellTaxBps: accepted.tokenMetadata.sellToken.sellTaxBps,
      }),
      fee: {
        totalNetworkFee: 'totalNetworkFee' in accepted ? accepted.totalNetworkFee : undefined,
        fees: accepted.fees,
      },
      isStale: result.isFetching || Boolean(result.data && !matchesCurrentSelection),
      raw: accepted,
      error: undefined,
      quoteError: undefined,
    };
  }, [
    accepted,
    buyToken,
    hasTradeInputs,
    lane,
    matchesCurrentSelection,
    queryArgs?.sellAmount,
    result.data,
    result.error,
    result.isFetching,
    sellToken,
    tokenUsdPrices,
  ]);
}
